import {
  Button,
  Container,
  Divider,
  Grid,
  Paper,
  Typography,
  Chip,
} from "@mui/material";
import UploadIcon from "@mui/icons-material/Publish";
import { useContext, useEffect, useRef, useState } from "react";
import { format } from "date-fns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AppContext } from "../context/AppProvider";
import { AuthContext } from "../context/AuthProvider";
import { cloneDeep } from "lodash";
import moment from "moment";
import { fetchWithInterceptor } from "../utils/fetchWithInterceptor";
import CustomDataGrid from "../components/CustomDataGrid";
import UploadCostCsvDialog from "../sections/cost-update-csv/UploadCostCsvDialog";

const PAGE_SIZE = 20;

const STATUS = {
  pending: "Pending",
  processing: "Processing",
  completed: "Completed",
  failed: "Failed",
};

const STATUS_COLOR = {
  pending: "warning",
  processing: "info",
  completed: "success",
  failed: "error",
};

const defaultFilter = {
  date: null,
};

export default function CostUpdateCSVs() {
  const appContext = useContext(AppContext);
  const authContext = useContext(AuthContext);

  const [dialogVisible, setDialogVisible] = useState(false);
  const [filter, setFilter] = useState(defaultFilter);

  const [rows, setRows] = useState([]);

  const [isLoading, setIsLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: PAGE_SIZE,
  });
  const [rowCount, setRowCount] = useState(0);

  const downloadFiles = async (rowId) => {
    try {
      const response = await fetchWithInterceptor(
        `/api/integrations/cost-update-file-log/${rowId}/download-zip`,
        {
          method: "GET",
          headers: {
            "x-access-token": authContext.getToken(),
          },
        },
      );
      if (response.ok) {
        const blob = await response.blob();
        const contentDisposition =
          response.headers.get("content-disposition") || "";
        const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
        const fileName = fileNameMatch?.[1] || `cost-update-files-${rowId}.zip`;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName;
        a.style.display = "none";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } else {
        console.log(response);
        appContext.showMessage(
          "There was an error downloading the files",
          "error",
        );
      }
    } catch (error) {
      console.log(error);
      appContext.showMessage(
        "There was an error downloading the files",
        "error",
      );
    }
  };

  const columns = [
    {
      field: "createdAt",
      headerName: "Uploaded at",
      width: 200,
      sortable: false,
      valueGetter: (value) =>
        value ? format(new Date(value), "yyyy-MM-dd hh:mm a") : "",
    },
    {
      field: "status",
      headerName: "Status",
      width: 250,
      sortable: false,
      renderCell: (params) => {
        return (
          <Chip
            label={STATUS[params.value]}
            size="small"
            variant="filled"
            color={STATUS_COLOR[params.value]}
          />
        );
      },
    },
    {
      field: "id",
      headerName: "Actions",
      width: 400,
      sortable: false,
      renderCell: (params) => {
        return (
          <Button
            color="primary"
            onClick={() => downloadFiles(params?.row?.id)}
            disabled={params.row?.status !== "completed"}
          >
            Download files
          </Button>
        );
      },
    },
  ];

  const showDialog = () => setDialogVisible(true);
  const hideDialog = (reload) => {
    setDialogVisible(false);
    if (reload) {
      loadProducts(true);
    }
  };

  const handleReset = () => setFilter(cloneDeep(defaultFilter));

  let active = useRef(false);

  const loadProducts = async (reset) => {
    setIsLoading(true);
    let filters = {
      limit: PAGE_SIZE,
      offset: reset ? 0 : paginationModel.page * PAGE_SIZE,
    };
    if (filter.date) {
      filters.dateFrom = moment(filter.date).startOf("day").format();
      filters.dateTo = moment(filter.date).endOf("day").format();
    }
    const params = new URLSearchParams(filters);
    const response = await fetchWithInterceptor(
      `/api/integrations/cost-update-file-log/?${params}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "x-access-token": authContext.getToken(),
        },
      },
    );
    if (response.ok) {
      if (!active.current) {
        return;
      }

      const data = await response.json();
      if (reset) {
        setPaginationModel({ ...paginationModel, page: 0 });
      }
      setRows(data.records);
      setRowCount(data.total);
    } else {
      console.log(response);
      appContext.showMessage(
        "There was an error getting the products",
        "error",
      );
    }
    setIsLoading(false);
  };

  useEffect(() => {
    active.current = true;

    return () => {
      active.current = false;
    };
  }, []);

  useEffect(() => {
    loadProducts(false);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel.page]);

  useEffect(() => {
    loadProducts(true);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  return (
    <>
      <Typography
        variant="body1"
        sx={{ ml: 3, mt: 3, mr: 5, mb: 1, color: "text.secondary" }}
      >
        Upload the AGLC "Product Price List and Details - F66074" file and the
        Cin7 "Product Prices by Supplier" file to compare the costs of the
        products. The system will identify any discrepancies and provide a
        detailed report for review.
      </Typography>
      <Container component="main" maxWidth="100%" sx={{ pb: 3 }}>
        <Paper
          sx={{ width: "100%", bgcolor: "background.paper", mt: 2, mb: 2 }}
        >
          <div style={{ padding: 16 }}>
            <Typography variant="h6">Search Filters</Typography>
            <br />
            <Grid
              container
              spacing={2}
              alignItems="center"
              justifyContent="space-between"
            >
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <DatePicker
                  label="Uploaded at"
                  value={filter.date}
                  onChange={(date) => setFilter({ ...filter, date })}
                  format="yyyy-MM-dd"
                  slotProps={{
                    textField: { variant: "outlined", fullWidth: true },
                  }}
                />
              </Grid>
              <Grid
                size={{ xs: 12, sm: 6, md: "auto" }}
                sx={{ display: "flex", justifyContent: "flex-end" }}
              >
                <Button onClick={handleReset}>RESET FILTERS</Button>
              </Grid>
            </Grid>
          </div>
          <Divider />

          <Grid
            container
            justifyContent="flex-end"
            style={{ padding: 16 }}
            spacing={1}
          >
            <Grid>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={showDialog}
                startIcon={<UploadIcon />}
              >
                Upload CSVs
              </Button>
            </Grid>
          </Grid>

          <CustomDataGrid
            rows={rows}
            columns={columns}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            rowCount={rowCount}
            loading={isLoading}
          />
        </Paper>
        <UploadCostCsvDialog open={dialogVisible} onClose={hideDialog} />
      </Container>
    </>
  );
}
